/**
 * AI 생성기 컨테이너 — 입력 초안 · 안내 · 자료 고르기 · 실행 · 결과 반응을 묶어 `AiGenerateDockUI` 에 넘긴다.
 * 문구는 props 로 받는다(번역 연결은 호출부 몫).
 */
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { trackEvent } from '@zep/logger/tracking';
import { useViewport } from '@/hooks';
import { useUser } from '@/lib/auth';
import { AcceptAiFileExtensions } from '@/shared/features/ai-quiz-generation/consts/fileExtensions';
import { useQuizList } from '../../../components/useQuizList';
import { type AiGenerateDockSourceType } from '../consts/aiGenerateDock.const';
import { useAiGenerateDockDraft } from '../hooks/useAiGenerateDockDraft';
import { useAiGenerateDockFileDrop } from '../hooks/useAiGenerateDockFileDrop';
import { useAiGenerateDockGate } from '../hooks/useAiGenerateDockGate';
import { useAiGenerateDockNotice } from '../hooks/useAiGenerateDockNotice';
import { useAiGenerateDockOnboarding } from '../hooks/useAiGenerateDockOnboarding';
import { useAiGenerateDockOpenPlace } from '../hooks/useAiGenerateDockOpenPlace';
import { useAiGenerateDockPreregistration } from '../hooks/useAiGenerateDockPreregistration';
import { type AiGenerateDockSettledRun, useAiGenerateDockRun } from '../hooks/useAiGenerateDockRun';
import { useAiGenerateDockRunningToast } from '../hooks/useAiGenerateDockRunningToast';
import { useAiGenerateDockSourceSelect } from '../hooks/useAiGenerateDockSourceSelect';
import { useAiGenerateDockUsage } from '../hooks/useAiGenerateDockUsage';
import { useVisualViewportInset } from '../hooks/useVisualViewportInset';
import { aiGenerateDockTabAtom, aiGenerateDockUiAtom } from '../stores/aiGenerateDock.atoms';
import { type AiGenerateDockCopy, type AiGenerateDockOnboardingCopy, type AiGenerateDockRunCopy } from '../types/aiGenerateDock.types';
import { getRemainingQuizSlots, resolveAiGenerateDockCount } from '../utils/aiGenerateDockCount';
import { type AiGenerateDockRunOutcome, resolveAiGenerateDockFeedback } from '../utils/aiGenerateDockFeedback';
import { canShowAiGenerateDockOnboarding } from '../utils/aiGenerateDockOnboarding';
import { applyAiGenerateDockRegistration } from '../utils/aiGenerateDockPreregistration';
import { aiGenerateDockToasts } from '../utils/aiGenerateDockToasts';
import { toAiGeneratorGate } from '../utils/aiGenerateDockTracking';
import { AiGenerateDockFileDropOverlay } from './AiGenerateDockFileDropOverlay';
import { AiGenerateDockUI } from './AiGenerateDockUI';
export type AiGenerateDockProps = {
  copy: AiGenerateDockCopy;
  runCopy: AiGenerateDockRunCopy;
  onboardingCopy: AiGenerateDockOnboardingCopy;
  /** 입력창이 보이나(aiGenerateDockInputShownAtom) — 닫혀 있어도 컴포넌트는 남아 쓰던 입력을 기억한다. */
  open: boolean;
  /** 화면에 보이나 — 채팅 패널·설정 단계로 숨겨진 동안엔 전역 파일 끌어놓기를 받지 않는다. */
  visible?: boolean;
  onClose: () => void;
  /** 생성 결과로 다시 물어야 할 때(실패·거절 안내) 입력창을 그 탭으로 다시 연다. */
  onReopen?: (sourceType: AiGenerateDockSourceType) => void;
  placement?: 'fixed' | 'static';
};
export const AiGenerateDock = ({
  copy,
  runCopy,
  onboardingCopy,
  open,
  visible = true,
  onClose,
  onReopen,
  placement = 'fixed'
}: AiGenerateDockProps) => {
  const {
    quizList
  } = useQuizList();
  const remainingSlots = getRemainingQuizSlots(quizList.length);
  const {
    user
  } = useUser();
  const usage = useAiGenerateDockUsage();
  const gate = useAiGenerateDockGate(usage);
  // 탭은 카드·입력창이 함께 쓴다 — 카드가 연 탭이 곧 지금 탭이다.
  const sourceType = useAtomValue(aiGenerateDockTabAtom);
  const dispatchUi = useSetAtom(aiGenerateDockUiAtom);
  const setSourceType = (tab: AiGenerateDockSourceType) => dispatchUi({
    type: 'selectTab',
    tab
  });
  const draft = useAiGenerateDockDraft(sourceType);
  const {
    notice,
    submitBlockedReason,
    showNotice,
    clearNotice
  } = useAiGenerateDockNotice({
    gate,
    sourceType,
    runCopy
  });
  const {
    attachFile,
    submitLink
  } = useAiGenerateDockSourceSelect({
    draft,
    sourceType,
    runCopy,
    showNotice,
    clearNotice
  });
  const openedPlace = useAiGenerateDockOpenPlace({
    open,
    gate,
    sourceType,
    remainingSlots,
    quizCount: quizList.length,
    usageRemaining: usage.remaining
  });
  const {
    claimRegistration,
    registrationViewOf
  } =
  // 게스트는 제출 전에 로그인으로 페이지를 떠나 등록을 쓸 수 없다 — 로그인 뒤에 등록한다.
  useAiGenerateDockPreregistration(draft.attachedSources, {
    enabled: !!user
  });
  const {
    runningCount,
    runningPhase,
    run,
    cancel
  } = useAiGenerateDockRun({
    openedPlace,
    // 모든 실행(다시 시도 포함)이 시작 전에 다른 미완료 사전 등록을 끊는다 — 같은 세션 턴과 겹치지 않게.
    // 여기선 끊기만 하고 돌려받은 등록은 쓰지 않는다.
    onBeforeRun: input => {
      void claimRegistration(input.source);
    },
    onSettled: (outcome, settled) => handleSettled(outcome, settled),
    selectedTab: sourceType,
    remainingUsage: usage.limited && usage.remaining !== null ? usage.remaining : -1
  });
  // 생성 중엔 입력창 대신 화면 아래 가운데 토스트 「문제 N개를 만들고 있어요 · 중단」 — 완료·중단 토스트와 같은 자리.
  useAiGenerateDockRunningToast({
    visible,
    runningCount,
    runningPhase,
    copy,
    onCancel: cancel
  });
  // 열린 채 문제가 가득 차면(입력창 밖 헤더의 임시 저장 불러오기 등) 입력창을 닫는다 — 가득 찬 입력창은 그리지
  // 않으므로, 닫지 않으면 보이지 않는 입력창이 열린 채로 남는다. 다시 열면 상한 토스트가 뜬다.
  const isFullWhileOpen = open && remainingSlots <= 0;
  useEffect(() => {
    if (isFullWhileOpen) onClose();
  }, [isFullWhileOpen, onClose]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // tablet-sm(768) 아래 — 조건 칩이 아이콘만 남는 폭과 같다. 키보드 보정도 이 폭에서만 건다.
  const {
    isMobile,
    isTabletSm
  } = useViewport();
  const layout = isMobile || isTabletSm ? 'narrow' : 'wide';
  // 키보드가 올라오면 입력창을 키보드 바로 위로 — 위치는 AI_GENERATE_DOCK_FIXED_INSET_CLASS 가 정한다.
  const {
    setNode: keyboardInsetRef
  } = useVisualViewportInset<HTMLDivElement>(placement === 'fixed' && layout === 'narrow');

  // 처음 열었을 때 코치마크 — 실행할 수 있는 넓은 화면에서만, 안내·생성 중이 아닐 때.
  // 조건 메뉴·시트가 열려 있는가 — 코치마크가 뜨기 전에 먼저 열었으면 고르는 동안 코치마크를 미룬다.
  const [conditionsMenuOpen, setConditionsMenuOpen] = useState(false);
  // 입력창이 닫히거나 숨으면 메뉴도 함께 사라진다(열림 콜백 없이) — 다시 열 때 열린 것으로 남지 않게 비운다.
  const isDockShown = open && visible;
  useEffect(() => {
    if (!isDockShown) setConditionsMenuOpen(false);
  }, [isDockShown]);
  const onboarding = useAiGenerateDockOnboarding({
    place: openedPlace,
    sourceType,
    eligible: canShowAiGenerateDockOnboarding({
      open,
      visible,
      layout,
      gateKind: gate.kind,
      running: runningCount !== null,
      hasNotice: notice !== null,
      conditionsMenuOpen
    })
  });

  // 칩에 사전 등록 상태를 반영한다 — 등록 중엔 진행 표시, 등록되면 링크 제목·영상 길이.
  const attachment = draft.dockSource ? applyAiGenerateDockRegistration(draft.dockSource.attachment, registrationViewOf(draft.dockSource.source)) : null;
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (file) attachFile(file, 'picker');
  };
  const submit = () => {
    const countState = resolveAiGenerateDockCount({
      remaining: remainingSlots,
      selected: draft.conditions.count
    });
    if (countState.locked) return;
    onboarding.end('submit');
    // 「문제 만들기」를 누르면 입력창은 닫히고, 진행은 생성 중 토스트와 목록 끝 대기 카드가 보여 준다.
    onClose();
    clearNotice();
    // 자료 탭이라도 붙은 자료가 없으면 입력칸 글로 주제 생성한다(링크를 쓰다 말고 실행해도 같다).
    const source = draft.dockSource?.source ?? {
      kind: 'none'
    };
    const pendingRegistration = claimRegistration(source);
    draft.markRun(source);
    void run({
      sourceType: draft.dockSource ? sourceType : 'query',
      topic: draft.topic,
      source,
      ...(pendingRegistration ? {
        pendingRegistration
      } : {}),
      quizTypes: draft.conditions.quizTypes,
      count: countState.count,
      difficulty: draft.conditions.difficulty
    });
  };

  /**
   * 실행 결과 반응 — 토스트 · 입력 비우기 · 안내 + 다시 열기. 실패 자리는 run 훅이 맡는다.
   * 문항이 이미 목록에 들어간 실행은 입력창을 다시 열지 않는다 — 방금 생긴 문항을 가리지 않게 안내도 토스트로만.
   */
  const handleSettled = (outcome: AiGenerateDockRunOutcome, {
    input,
    applied
  }: AiGenerateDockSettledRun) => {
    // 문항이 들어온 뒤 끝났다(중단·오류 모두) — 들어온 문항이 그 실행의 결과다. 입력을 비워 같은 요청을
    // 다시 보내 비슷한 문항이 겹쳐 생기지 않게 한다(그 뒤 새로 쓴 초안은 남긴다). 오류 안내는 아래에서 토스트로.
    if (applied && draft.isDraftOf(input)) draft.clear();
    // 중단은 들어온 문항이 결과라 따로 알리지 않는다.
    if (outcome.kind === 'aborted' && applied) return;
    const feedback = resolveAiGenerateDockFeedback(outcome, runCopy);
    if (feedback.usage) usage.update(feedback.usage);else if (feedback.quotaExhausted) usage.markExhausted();
    if (feedback.toast) aiGenerateDockToasts.success(feedback.toast);
    if (feedback.infoToast) aiGenerateDockToasts.info(feedback.infoToast);
    // 결과가 목록에 들어갔다 — 다음은 새 요청이라 입력을 비운다. 단 그 실행 뒤 새로 쓴 초안(다른 주제·자료)은
    // 지우지 않는다 — 지난 실행의 「다시 시도」가 성공해도 지금 쓰는 입력은 남는다.
    if (feedback.resetInput && draft.isDraftOf(input)) {
      draft.clear();
      return;
    }
    // 생성 자체가 실패하면 대기 카드가 실패 카드로 남는다(run 훅이 실행 상태로 둔다).
    if (feedback.failedCards) return;
    if (!feedback.notice) return;
    if (applied) {
      if (feedback.notice.tone === 'destructive') {
        aiGenerateDockToasts.error(feedback.notice.message);
      } else {
        aiGenerateDockToasts.info(feedback.notice.message);
      }
      return;
    }
    showNotice({
      tone: feedback.notice.tone,
      message: feedback.notice.message,
      ...(feedback.notice.retry ? {
        action: {
          label: runCopy.retry,
          // 처음 제출과 같이 입력창을 닫고 실행한다 — 성공 뒤 빈 입력창·뒤 잠금이 다시 뜨지 않게.
          onClick: () => {
            onClose();
            clearNotice();
            void run(input, 'notice_retry');
          }
        }
      } : {})
    }, input.sourceType);
    onReopen?.(input.sourceType);
  };
  const {
    isDraggingFile
  } = useAiGenerateDockFileDrop({
    // 화면에 실제로 입력창이 그려질 때만 — 20문제가 가득하면 입력창을 그리지 않아 받을 수도, 실행할 수도 없다.
    enabled: visible && open && remainingSlots > 0,
    onDropFile: file => {
      // 어느 탭에서 놓아도 파일 탭으로 옮겨 붙인다(PRD 3-2 「놓으면 바로 첨부」).
      setSourceType('file');
      attachFile(file, 'drop');
    }
  });
  return <>
      <AiGenerateDockUI copy={copy} placement={placement} onClose={() => {
      // ✕·Esc 로 실행 없이 닫았다 — 실행하며 닫히는 경우(submit)는 run_submit 이 대신한다.
      trackEvent('quiz_ai_generator_dock_close', {
        place: openedPlace,
        gate: toAiGeneratorGate(gate.kind),
        remaining_slot_count: remainingSlots,
        quiz_count: quizList.length,
        source_type: sourceType,
        has_topic: draft.topic.trim().length > 0,
        has_attachment: draft.dockSource !== undefined,
        destination: 'build_page'
      });
      onboarding.end('close');
      onClose();
    }} sourceType={sourceType} onSourceTypeChange={next => {
      // 탭을 바꿔도 코치마크는 그 단계 그대로 — 2단계(자료 탭)가 바로 탭을 눌러 보라는 안내다.
      clearNotice();
      setSourceType(next);
    }} topic={draft.topic} onTopicChange={draft.setTopic} linkUrl={draft.linkUrl} onLinkUrlChange={draft.setLinkUrl} onLinkSubmit={submitLink} attachment={attachment} onAttachFile={() => fileInputRef.current?.click()} onRemoveAttachment={draft.clearSource} conditions={draft.conditions} onConditionsChange={draft.setConditions} remainingSlots={remainingSlots} open={open} visible={visible} layout={layout} rootRef={keyboardInsetRef} onSubmit={submit} submitBlockedReason={submitBlockedReason} usageHint={usage.limited && usage.remaining !== null ? runCopy.usageRemaining(usage.remaining) : undefined} notice={notice} onConditionsOpenChange={nextOpen => {
      setConditionsMenuOpen(nextOpen);
      if (nextOpen) onboarding.end('conditions_open');
    }} onboarding={onboarding.step && {
      step: onboarding.step,
      copy: onboardingCopy,
      onNext: onboarding.next,
      onEscape: () => onboarding.end('escape')
    }} data-sentry-element="AiGenerateDockUI" data-sentry-source-file="AiGenerateDock.tsx" />
      {isDraggingFile && <AiGenerateDockFileDropOverlay message={copy.dropFile} placement={placement} />}
      <input ref={fileInputRef} type="file" accept={AcceptAiFileExtensions} className="hidden" data-testid="ai-generate-dock-file-input" onChange={handleFileChange} />
    </>;
};